import React from "react";
import { stringifyBase64 } from "@/hooks/useQueryParamState";

import { PlanIdTypeSafe } from "@/types/PlanTypes";
import usePlan from "@/hooks/usePlan";
import useBandwidth from "@/components/newcustomize/ProxyDetails/BandWidth/hooks/useBandwidth";
import getProxyPlanKey from "@/utils/getProxyPlanKey";
import useProxyCount from "@/components/newcustomize/ProxyDetails/ProxyCount/hooks/useProxyCount";

type RecommendBandwidth = {
  menu: string;
  recommend_bandwidth: number;
};

type RecommedProxyCount = {
  menu: string;
  recommend_proxyCount: number;
};

type UseRecommendedReturnType = {
  bandwidth: RecommendBandwidth;
  proxyCount: RecommedProxyCount;
};

export default function useRecommended({
  planId,
}: {
  planId?: PlanIdTypeSafe;
} = {}): UseRecommendedReturnType {
  // NOT OK: This should use the relevant plan from the notification, but we are waiting for backend support: https://gitlab.com/unified-proxy/frontend/proxycontrolpaneldashboard-container/-/issues/2487
  const { data: plan } = usePlan(planId || "default");

  const proxyPlanKey = getProxyPlanKey({
    proxy_type: plan?.proxy_type || "shared",
    proxy_subtype: plan?.proxy_subtype || "default",
    proxy_count: plan?.proxy_count,
  });

  const { mapping: bandwidthMenuItems } = useBandwidth({
    onlyProxyPlan: proxyPlanKey,
  });

  const { mapping: proxyCountMenuItems } = useProxyCount({
    onlyProxyPlan: proxyPlanKey,
  });

  const bandwidth = React.useMemo((): RecommendBandwidth => {
    if (!plan || (plan && plan.proxy_type === "free")) {
      const bandwidth = 250;
      return {
        menu: stringifyBase64({
          selectedBandwidth: bandwidth,
          selectedNumberOfProxies: plan?.proxy_count,
        }),
        recommend_bandwidth: bandwidth,
      };
    }

    const pickBandwidth = bandwidthMenuItems
      .map((item) => item.bandwidth)
      .filter((item) => item > plan.bandwidth_limit);

    const bandwidth =
      pickBandwidth && pickBandwidth.length > 0
        ? Number(pickBandwidth.at(0))
        : 0;

    return {
      menu: stringifyBase64({
        selectedBandwidth: bandwidth,
        selectedNumberOfProxies: plan?.proxy_count,
      }),
      recommend_bandwidth: bandwidth,
    };
  }, [bandwidthMenuItems, plan]);

  // memoize recommendation: tier above the current plan
  const proxyCount = React.useMemo((): RecommedProxyCount => {
    if (!plan || (plan && plan.proxy_type === "free")) {
      const numberOfProxies = 250;
      return {
        menu: stringifyBase64({
          selectedBandwidth: plan?.bandwidth_limit,
          selectedNumberOfProxies: numberOfProxies,
        }),
        recommend_proxyCount: numberOfProxies,
      };
    }

    const pickProxyCount = proxyCountMenuItems
      .map((item) => item.proxyCount)
      .filter((item) => item > plan.proxy_count);

    const selectedNumberOfProxies =
      pickProxyCount && pickProxyCount.length > 0
        ? Number(pickProxyCount.at(0))
        : plan.proxy_count;

    return {
      menu: stringifyBase64({
        selectedBandwidth: plan?.bandwidth_limit,
        selectedNumberOfProxies,
      }),
      recommend_proxyCount: selectedNumberOfProxies,
    };
  }, [plan, proxyCountMenuItems]);

  return {
    bandwidth,
    proxyCount,
  };
}
